import Modal from '@components/modal/Modal';
import cx from 'classnames';
import i18n from 'i18next';
import { useEffect, useRef, useState } from 'react';
import { MdClose, MdLanguage } from 'react-icons/md';
import { connect } from 'react-redux';

import { updateUserConfig } from '../actions';
import { AnimatedButton } from '../chaosComponentsV2/button/AnimatedButton';
import {
  LANGUAGE_SELECTED_STORAGE_KEY,
  getLocalStorageValue,
  setLocalStorage,
} from '../helpers/localStorage';

const LANGUAGE_PREFERENCE_STORAGE_KEY = 'languagePreference';

const createFlagIcon =
  (emoji: string) =>
  ({ className }: { className?: string }) => (
    <span
      className={cx(
        className,
        'tw-inline-flex tw-items-center tw-justify-center tw-text-base tw-not-italic',
      )}
    >
      {emoji}
    </span>
  );

const FlagGB = createFlagIcon('🇬🇧');
const FlagFI = createFlagIcon('🇫🇮');

const LANGUAGE_OPTIONS = [
  { code: 'en', label: 'English', icon: FlagGB },
  { code: 'fi', label: 'Suomi', icon: FlagFI },
];

type ILanguageSelectionModal = {
  isAuthenticated: boolean;
  updateUserConfigAction: (partialUserConfig: object) => void;
};

const LanguageSelectionModal = ({
  isAuthenticated,
  updateUserConfigAction,
}: ILanguageSelectionModal) => {
  const [isOpen, setIsOpen] = useState(
    () => !getLocalStorageValue(LANGUAGE_SELECTED_STORAGE_KEY),
  );

  const wasAuthenticatedRef = useRef(isAuthenticated);

  // Carries a language chosen while logged out onto the account the next time
  // the user logs in, since the backend's stored setting would otherwise win
  // and silently revert the choice made on the login screen.
  useEffect(() => {
    const justLoggedIn = !wasAuthenticatedRef.current && isAuthenticated;
    wasAuthenticatedRef.current = isAuthenticated;

    if (!justLoggedIn) return;

    const preferredLanguage = getLocalStorageValue(
      LANGUAGE_PREFERENCE_STORAGE_KEY,
    );
    if (preferredLanguage) {
      i18n.changeLanguage(preferredLanguage);
      updateUserConfigAction({ language: preferredLanguage });
      window.localStorage.removeItem(LANGUAGE_PREFERENCE_STORAGE_KEY);
    }
  }, [isAuthenticated, updateUserConfigAction]);

  const dismiss = () => {
    setLocalStorage(LANGUAGE_SELECTED_STORAGE_KEY, 'true');
    window.dispatchEvent(new Event(LANGUAGE_SELECTED_STORAGE_KEY));
    setIsOpen(false);
  };

  const handleSelectLanguage = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    setLocalStorage(LANGUAGE_PREFERENCE_STORAGE_KEY, languageCode);
    if (isAuthenticated) {
      updateUserConfigAction({ language: languageCode });
    }
    dismiss();
  };

  const renderLanguageSelectionContent = () => (
    <div className="tw-relative tw-flex tw-flex-col tw-gap-7 tw-w-[26rem] tw-py-5">
      <MdClose
        size={18}
        className="tw-absolute tw-top-0 tw-right-0 tw-cursor-pointer tw-text-weaker"
        onClick={dismiss}
      />
      <div className="tw-flex tw-items-center tw-gap-2">
        <MdLanguage size={22} color="var(--color-accent)" />
        <span className="tw-font-semibold tw-text-secondary tw-text-lg">
          Choose your language
        </span>
      </div>
      <span className="tw-text-sm tw-text-weaker">Valitse kieli</span>
      <div className="tw-flex tw-flex-col tw-gap-3">
        {LANGUAGE_OPTIONS.map(({ code, label, icon }) => (
          <AnimatedButton
            key={code}
            type="button"
            variant="outline"
            label={label}
            icon={icon}
            iconPosition="right"
            fullWidth
            onClick={() => handleSelectLanguage(code)}
            className="!tw-px-4 !tw-py-3"
            iconClassName="!tw-w-4 !tw-h-4"
          />
        ))}
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      renderModalContent={renderLanguageSelectionContent}
      onHandleCancel={dismiss}
      hideCancelButton
      hideCloseButton
      renderFooter={false}
      maskClosable
    />
  );
};

const mapStateToProps = (state: any) => ({
  isAuthenticated: state.auth.authenticated,
});

export default connect(mapStateToProps, {
  updateUserConfigAction: updateUserConfig,
})(LanguageSelectionModal);
